import type { ReactNode } from "react";
import type { LayoutItem } from "react-grid-layout/legacy";
import type {
  ComponentSpec,
  MessagePart,
  ProcessedWidgetData,
  WidgetResponseSpec,
  MetricOption,
  SubjectEntity,
} from "./Processing/types";
import type { PdfWidgetSnapshot } from "./PDF/pdfTypes";
import type { SlicerValues } from "./Utils/resolveTemplate";
import type { PdfRect } from "./PDF/pdfLayout";
import type {
  DuplicationHandler,
  ControlPanelHandler,
  InfoHandler,
  WidgetDescription,
  NoDuplication,
  NoControlPanel,
  NoInfo,
  NoPdf,
} from "./widgetContract";
import type { WidgetSlots, NoSlots } from "./Slots";
import type { ControllerKind } from "./Processing/types";

/** Props passed to every grid widget component. */
export interface GridWidgetComponentProps {
  id: string;
}

/**
 * Shared props passed to every configurator component. Generic over the widget's
 * spec type so a configurator narrows `onSpecChange`/`existingSpec` to its own spec
 * (e.g. `ConfiguratorSharedProps<TitleComponentSpec>`) — one contract, no per-widget
 * `<Name>ConfiguratorProps` duplicates.
 */
export interface ConfiguratorSharedProps<TSpec extends ComponentSpec = ComponentSpec> {
  onSpecChange: (spec: TSpec | null) => void;
  existingSpec?: TSpec;
  /** Runtime id of the widget being edited; absent while adding a new one. */
  widgetId?: string;
  initialFormState?: unknown;
  metrics: MetricOption[];
  getSubjectEntities: (subjectType: string) => SubjectEntity[];
}

/**
 * Context passed to PDF snapshot builders.
 *
 * `buildPdfSnapshots` (PDF/pdfTypes.ts) fills it from the DashboardState. The
 * headless renderer in the Lambda repo builds that state by hand with `widgets`,
 * `processedData` and `aiWidgets` only. A new field here must come from one of
 * those, or the Lambda's `render.ts` must grow with it in the same change set. A
 * core's buildSnapshot must treat every optional field as absent on the server
 * and degrade to an empty tile, never throw.
 */
export interface PdfSnapshotContext {
  processed: ProcessedWidgetData;
  teamLogo?: string;
  /** Streamed answer parts for this tile (not processedData) — ai_insights only. */
  aiParts?: MessagePart[];
  /** Resolves subject/drill ids to display names — the PDF has no Redux access at render. */
  getSubjectEntities?: (subjectType: string) => SubjectEntity[];
  /** The slicer value map a title or text token quotes; absent, the tokens render literal. */
  slicerValues?: SlicerValues;
}

/** Context passed to `prepareSnapshot`: how to turn a permissioned image ref into a data URI. */
export interface PdfPrepareContext {
  /** Resolves an image ref (e.g. a headshot URL) to a data URI, or undefined on failure. */
  fetchImageAsDataUri: (ref: string) => Promise<string | undefined>;
}

/** Context passed to PDF renderers. */
export interface PdfRenderContext {
  id: string;
  snapshot: PdfWidgetSnapshot;
  rect: PdfRect;
  scale: number;
  chartImages: Record<string, string>;
}

/**
 * How the headless renderer rasterizes a widget's image area before PDF export:
 * - "highcharts" — the widget's Highcharts options are drawn and exported as SVG.
 * - "dom" — the widget is drawn from its own SVG builder (the body map).
 * Omit when the widget's PDF renderer draws itself and needs no captured image.
 */
export type PdfCaptureMode = "highcharts" | "dom";

/** A widget's PDF export support. */
export interface PdfSupport {
  buildSnapshot: (ctx: PdfSnapshotContext) => PdfWidgetSnapshot | null;
  render: (ctx: PdfRenderContext) => React.JSX.Element | null;
  /** Image-capture mode for this widget's area; omit for no captured image. */
  capture?: PdfCaptureMode;
  /** Async fixups on the snapshot before capture (e.g. normalize a remote headshot). */
  prepareSnapshot?: (snapshot: PdfWidgetSnapshot, ctx: PdfPrepareContext) => Promise<void>;
}

/**
 * Complete definition of a widget type. All per-type knowledge lives here.
 *
 * To add a new widget type:
 * 1. Create the grid component in WidgetComponents/
 * 2. Optionally create a configurator in AddWidgetModal/
 * 3. Optionally create a PDF renderer in PDF/renderers/
 * 4. Create a definition file in definitions/ implementing this interface
 * 5. Register it in widgetRegistry.ts
 */
export interface WidgetDefinition<TSpec extends ComponentSpec = ComponentSpec> {
  /** Unique type key, narrowed to this widget's spec discriminant. */
  type: TSpec["type"];

  /**
   * Whether this widget fetches data from the backend. When false (or omitted),
   * the widget is processed locally on add and never triggers a backend query.
   * Replaces the former hand-maintained `needsBackendData()` allowlist so this
   * fact lives on the definition and can't drift out of sync.
   */
  needsData?: boolean;

  /** Template picker metadata. */
  template: {
    label: string;
    description: string;
    icon: ReactNode;
  };

  /** Default grid layout dimensions for new instances. */
  defaultLayout: Omit<LayoutItem, "i" | "x" | "y">;

  /** The React component rendered in the grid. */
  gridComponent: (props: GridWidgetComponentProps) => ReactNode;

  /** Process raw API response into widget-specific data. Omit for widgets that need no processing. */
  process?: (
    spec: TSpec,
    response: WidgetResponseSpec | null,
  ) => Extract<ProcessedWidgetData, { type: TSpec["type"] }>;

  /** Configurator shown in the AddWidgetModal. Omit for widgets with no configuration UI. */
  configurator?: {
    modalTitle: string;
    wide: boolean;
    Component: React.ComponentType<ConfiguratorSharedProps>;
  };

  /** PDF export support, or the explicit `NoPdf` opt-out for widgets not in PDF. */
  pdf?: PdfSupport | NoPdf;

  // ── Widget contract (see widgetContract.ts + Docs/WIDGET_CONTRACT_DESIGN.md) ──
  // Optional during migration; the plan is to flip these to required once every
  // widget implements them, so the Record<> registry enforces the whole contract.

  /** Declared spec-field descriptor — the hash input; also the future AI-prompt source. */
  schema?: readonly string[];

  /**
   * ID-bearing slot descriptor: the metric- / subject- / drill-id locations in this
   * widget's spec that the dashboard template (de)serializer tokenizes on export and
   * resolves on import, or the `NoSlots` opt-out for widgets that carry no ids
   * (title/text/logo/spacer). Colocated here so the whole per-type contract lives on
   * the definition; the implementations live in the component-free `Slots/` module,
   * which `Templates/` imports directly via `getWidgetSlots` (without pulling in the
   * widget component / PDF graph), and which this field references. `WidgetSlots<TSpec>`
   * compile-checks that a definition can only hold its own widget's descriptor.
   *
   * Required — the first fully-migrated contract field (the `Record<GridWidgetType,
   * WidgetDefinition>` registry now forces every widget to declare slots or opt out).
   */
  slots: WidgetSlots<TSpec> | NoSlots;

  /** (b) Regenerate the spec's queryIds on duplicate/import, or `NoDuplication`. */
  duplicate?: DuplicationHandler<TSpec> | NoDuplication;

  /** (a) Apply a Control Panel date/subject selection, or `NoControlPanel`. */
  controlPanel?: ControlPanelHandler<TSpec> | NoControlPanel;

  /** (f) Info-popover content, or the explicit `NoInfo` opt-out. */
  info?: InfoHandler | NoInfo;

  /** (g) Placeholder — AI "Build my Dashboard" description. Not yet enabled. */
  describe?: WidgetDescription;

  /**
   * (g) Dynamic behavior: which of this widget's filter sections may follow a Dynamic
   * Controller. The shared date/subject forms show their "Dynamically controlled" row only
   * for the sections listed here, and the Control Panel skips a section while it is bound.
   * Omit for widgets that cannot be governed (body_map, player_card, layout types).
   */
  dynamic?: {
    bindableSections: readonly ControllerKind[];
  };

  /** (h) Review hash pasted from the admin checklist; verified on load. */
  contractHash?: string;
}

/**
 * Authors a widget definition with per-type safety — its `process` sees the
 * narrow `TSpec` and must return the matching `ProcessedWidgetData` arm — then
 * stores it under the wide `WidgetDefinition` so the registry stays homogeneous.
 * The single localized cast here replaces the per-definition `spec as XSpec` casts.
 */
export function defineWidget<TSpec extends ComponentSpec>(
  def: WidgetDefinition<TSpec>,
): WidgetDefinition {
  return def as unknown as WidgetDefinition;
}

/**
 * The component-free half of a widget definition: how a response becomes processed
 * data, and how processed data becomes a PDF. Each `Definitions/core/<type>.ts` exports
 * one, the React definition spreads it, and `Definitions/core/index.ts` indexes them so
 * the pipeline, the PDF export and the headless renderer never import a widget component.
 */
export type WidgetCore<TSpec extends ComponentSpec = ComponentSpec> = Pick<
  WidgetDefinition<TSpec>,
  "process" | "pdf"
>;

/** Authors a widget core with the same per-type narrowing as `defineWidget`. */
export function defineWidgetCore<TSpec extends ComponentSpec>(
  core: WidgetCore<TSpec>,
): WidgetCore<TSpec> {
  return core;
}

/**
 * Narrow a definition's `pdf` field to real support, or null when it's absent or the
 * `NoPdf` opt-out. `"buildSnapshot" in pdf` discriminates the handler from the sentinel.
 */
export function pdfSupport(pdf: PdfSupport | NoPdf | undefined): PdfSupport | null {
  return pdf && "buildSnapshot" in pdf ? pdf : null;
}
